import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ConfigProvider, Spin } from "antd";
import enUS from "antd/lib/locale/en_US";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { logger } from "./services/logger";
import { IconContext } from "react-icons";
import { TaxYearInitializer } from "./TaxYearInitializer";
import { USER_PERMISSIONS } from "./config/Permissions ";

// Lazy-loaded components for better performance

const NotFoundPage = React.lazy(() => import("./pages/notfound/NotFound"));
const ForbiddenPage = React.lazy(() => import("./pages/forbidden/Forbidden"));
const Login = React.lazy(() => import("./pages/login/Login"));
const MainLayout = React.lazy(() => import("./layouts/MainLayout"));
const AuthGuard = React.lazy(() => import("./routes/AuthGuards"));

const ClientManagement = React.lazy(
  () => import("./pages/clientManagement/ClientManagement"),
);
const FeinManagement = React.lazy(
  () => import("./pages/feinManagement/feinManagement"),
);

const ApprovalProcess = React.lazy(
  () => import("./pages/approvalManagement/index"),
);

const PrintHistory = React.lazy(
  () => import("./pages/printFiling/clientManagementHistoryTabs"),
);

const CodeReviewReport = React.lazy(
  () => import("./pages/codeReviewReport/index"),
);

const FeinReviewReport = React.lazy(
  () => import("./pages/feinReviewReport/index"),
);

const SOC1AuditReport = React.lazy(
  () => import("./pages/soc1AuditReport/index"),
);

const StateReport = React.lazy(() => import("./pages/stateReport/index"));

const CompanyAverageFTECountReport = React.lazy(
  () => import("./pages/companyAverageFTECountReport/index"),
);

const ValidationErrorReport = React.lazy(
  () => import("./pages/validationErrorReport/index"),
);

const AcaTransmissionValidationReport = React.lazy(
  () => import("./pages/acaTransmissionValidationReport/index"),
);

const CaTransmissionValidationReport = React.lazy(
  () => import("./pages/caTransmissionValidationReport/index"),
);
const ValidationErrorsSummaryReport = React.lazy(
  () => import("./pages/validationErrorsSummaryReport/index"),
);

const PrintCountsMonthlySummaryReport = React.lazy(
  () => import("./pages/printCountsMonthlySummaryReport/index"),
);

const HoldFormsReport = React.lazy(
  () => import("./pages/holdFormsReport/index"),
);

const CompanySettingsPartIPartIISummaryReport = React.lazy(
  () => import("./pages/companySettingsPartIPartIISummaryReport/index"),
);

const EmployeePartIICensusReport = React.lazy(
  () => import("./pages/employeePartIICensusReport/index"),
);

const EmployeeManualChangeReport = React.lazy(
  () => import("./pages/employeeManualChangeReport/index"),
);

const CompanySettingsPartIIISummaryReport = React.lazy(
  () => import("./pages/companySettingsPart3SummaryReport/index"),
);

const TransmissionStatusReport = React.lazy(
  () => import("./pages/transmissionStatus/index"),
);

const CaTransmissionErrorsReport = React.lazy(
  () => import("./pages/caTransmissionErrorDetailsReport/index"),
);
const IRSErrorReport = React.lazy(() => import("./pages/irsErrorReport/index"));

const ChangeReportView = React.lazy(
  () => import("./pages/employeeMaterialNonMaterial/index"),
);

const ReportDownloadStatus = React.lazy(
  () => import("./pages/ReportDownloadStatus/index"),
);

const FormManagement = React.lazy(() => import("./pages/formManagement/index"));
const Form1094C = React.lazy(() => import("./pages/1094CForm/index"));
const Form1095C = React.lazy(() => import("./pages/1095CForm/index"));
const NewForm1095C = React.lazy(() => import("./pages/1095CForm/new/index"));

const ClientSettings = React.lazy(
  () => import("./pages/clientSettings/ClientSettings"),
);
const FileHistory = React.lazy(
  () => import("./pages/filehistory/FilesManagement"),
);

const PrintBatchingDashboard = React.lazy(
  () => import("./pages/printBatching/BatchManagement"),
);

const PrintIndividual = React.lazy(() => import("./pages/printRequest/index"));

const PrintClientDashboard = React.lazy(
  () => import("./pages/printFiling/printClientOverview"),
);

const FilingDashboard = React.lazy(
  () => import("./pages/filingManagement/FilingDashboard"),
);

const UserManagement = React.lazy(() => import("./pages/userManagement/index"));

const Reporting = React.lazy(() => import("./pages/reporting/index"));

const AccountSettings = React.lazy(
  () => import("./pages/accountSettings/index"),
);

const UserSettings = React.lazy(
  () => import("./pages/userSettings/UserSettings"),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: "always",
      retry: 0,
      staleTime: 1 * 60 * 1000,
      gcTime: 2 * 60 * 1000,
      refetchOnMount: true,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      logger.error("ReactQuery", error.message, error.stack);
    },
  }),
});

const isDev = process.env.NODE_ENV === "development";

function App() {
  return (
    <IconContext.Provider
      value={{ size: "20px", color: "rgba(0, 0, 0, 0.88)" }}
    >
      <ConfigProvider
        locale={enUS}
        theme={{
          token: {
            colorPrimary: "#7f35b2",
            fontFamily:
              '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
            fontSize: 14,
            colorSuccessText: "#0B6951",
            colorSuccessBg: "#E9F7F2",
            colorErrorText: "#B2242E",
            colorErrorBg: "#FFF0F0",
            colorWarning: "#845200",
            colorWarningBg: "#FFF3D9",
            colorInfoText: "#074CAD",
            colorInfoBorder: "#074CAD",
            colorInfoBg: "#ECF5FF",
          },
          components: {
            Menu: {
              horizontalItemHoverBg: "rgba(255, 255, 255, 0.15)",
              horizontalItemSelectedBg: "#e6e6e6",
              horizontalItemSelectedColor: "#48086f",
              itemSelectedColor: "#48086f",
              itemColor: "#fff",
              itemHoverColor: "#fff",
              colorPrimary: "#48086f",
              colorBgContainer: "#48086f",
              fontSize: 16,
              popupBg: "#48086f",
            },
            Layout: {
              headerBg: "#48086f",
              headerColor: "#fff",
              headerPadding: "0 8px 0 20px",
              headerHeight: 56,
              fontSize: 18,
              footerPadding: "6px",
              bodyBg: "#e6e6e6",
              footerBg: "rgb(191,191,191,0.4)",
            },
            Card: {
              colorBgContainer: "#fff",
              colorTextHeading: "black",
              colorText: "black",
              colorTextDescription: "black",
              bodyPadding: 18,
              headerPadding: 12,
              headerHeight: 42,
            },
            Table: {
              //"rgb(191,191,191,0.6)"
              headerBg: "#c2a8f0",
              borderColor: "rgb(127, 53, 178,0.2)",
              headerBorderRadius: 4,
              cellPaddingBlockMD: 10,
              cellPaddingInlineMD: 10,
              borderRadius: 8,
              headerColor: "black",
              footerBg: "#fafafa",
            },
            Form: {
              itemMarginBottom: 12,
            },
            Alert: {
              colorErrorBg: "#FEE3E4",
            },
            Button: {
              defaultBg: "#f6f6f6",
            },
            Timeline: {
              dotBg: "#fafafa",
              itemPaddingBottom: 6,
            },
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Suspense fallback={<Spin tip="Loading..." fullscreen />}>
              <TaxYearInitializer />
              <Routes>
                {/* Redirect root path to /clientmanagement */}
                <Route
                  path="/"
                  element={<Navigate to="/clientmanagement" replace />}
                />

                {/* Main layout with nested routes */}
                <Route path="/" element={<MainLayout />}>
                  <Route
                    path="/clientmanagement"
                    element={<AuthGuard element={<ClientManagement />} />}
                  />
                  <Route
                    path="/clientmanagement/new"
                    element={
                      <AuthGuard
                        element={<ClientSettings />}
                        permission={[
                          USER_PERMISSIONS.ViewClient,
                          USER_PERMISSIONS.ViewStateFiling,
                          USER_PERMISSIONS.ViewAdditionalAlems,
                          USER_PERMISSIONS.ViewAlternateReturnAddress,
                        ]}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/:clientId"
                    element={
                      <AuthGuard
                        element={<ClientSettings />}
                        permission={[
                          USER_PERMISSIONS.ViewClient,
                          USER_PERMISSIONS.ViewStateFiling,
                          USER_PERMISSIONS.ViewAdditionalAlems,
                          USER_PERMISSIONS.ViewAlternateReturnAddress,
                        ]}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/fein/:clientId"
                    element={
                      <AuthGuard
                        element={<FeinManagement />}
                        permission={USER_PERMISSIONS.View1094C}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/employee/:clientId"
                    element={
                      <AuthGuard
                        element={<FormManagement />}
                        permission={USER_PERMISSIONS.View1095C}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/fein/:clientId/:feinId"
                    element={
                      <AuthGuard
                        element={<Form1094C />}
                        permission={USER_PERMISSIONS.View1094C}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/employee/:clientId/new"
                    element={
                      <AuthGuard
                        element={<NewForm1095C />}
                        permission={USER_PERMISSIONS.Create1095C}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/employee/:clientId/:formId"
                    element={
                      <AuthGuard
                        element={<Form1095C />}
                        permission={USER_PERMISSIONS.View1095C}
                      />
                    }
                  />
                  <Route
                    path="/printfiling"
                    element={
                      <AuthGuard
                        element={<PrintBatchingDashboard />}
                        permission={
                          USER_PERMISSIONS.PrintAndFilingActivityRestricted
                        }
                      />
                    }
                  />
                  <Route
                    path="/printRequest"
                    element={
                      <AuthGuard
                        element={<PrintIndividual />}
                        permission={USER_PERMISSIONS.ViewPrintIndividual}
                      />
                    }
                  />
                  <Route
                    path="/printfiling/print/:clientId/:taxYear"
                    element={
                      <AuthGuard
                        element={<PrintClientDashboard />}
                        permission={
                          USER_PERMISSIONS.PrintAndFilingActivityRestricted
                        }
                      />
                    }
                  />

                  <Route
                    path="/printfiling/filing/:clientId/:taxYear"
                    element={
                      <AuthGuard
                        element={<FilingDashboard />}
                        permission={
                          USER_PERMISSIONS.PrintAndFilingActivityRestricted
                        }
                      />
                    }
                  />
                  <Route
                    path="/filemanagement"
                    element={
                      <AuthGuard
                        element={<FileHistory />}
                        permission={USER_PERMISSIONS.ViewFileImportHistory}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/approvals/:clientId/:taxYear"
                    element={
                      <AuthGuard
                        element={<ApprovalProcess />}
                        permission={[
                          USER_PERMISSIONS.View1095CApproval,
                          USER_PERMISSIONS.View1094CApproval,
                        ]}
                      />
                    }
                  />
                  <Route
                    path="/clientmanagement/history/:clientId/:taxYear"
                    element={
                      <AuthGuard
                        element={<PrintHistory />}
                        permission={USER_PERMISSIONS.PrintAndFilingActivity}
                      />
                    }
                  />
                  <Route
                    path="/reporting"
                    element={
                      <AuthGuard
                        element={<Reporting />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/1"
                    element={
                      <AuthGuard
                        element={<CodeReviewReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/2"
                    element={
                      <AuthGuard
                        element={<FeinReviewReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/3"
                    element={
                      <AuthGuard
                        element={<SOC1AuditReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/4"
                    element={
                      <AuthGuard
                        element={<StateReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/5"
                    element={
                      <AuthGuard
                        element={<CompanyAverageFTECountReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/6"
                    element={
                      <AuthGuard
                        element={<ValidationErrorReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/8"
                    element={
                      <AuthGuard
                        element={<HoldFormsReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/9"
                    element={
                      <AuthGuard
                        element={<PrintCountsMonthlySummaryReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/10"
                    element={
                      <AuthGuard
                        element={<CompanySettingsPartIPartIISummaryReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/11"
                    element={
                      <AuthGuard
                        element={<CompanySettingsPartIIISummaryReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/13"
                    element={
                      <AuthGuard
                        element={<IRSErrorReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/14"
                    element={
                      <AuthGuard
                        element={<ChangeReportView />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/12"
                    element={
                        <AuthGuard element={<TransmissionStatusReport />}
                            permission={USER_PERMISSIONS.ViewRestrictedReports} />
                    }
                  />
                  <Route
                    path="/reporting/report/15"
                    element={
                      <AuthGuard
                        element={<AcaTransmissionValidationReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/16"
                    element={
                      <AuthGuard
                        element={<CaTransmissionValidationReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/17"
                    element={
                      <AuthGuard
                        element={<EmployeePartIICensusReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/18"
                    element={
                      <AuthGuard
                        element={<CaTransmissionErrorsReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/report/19"
                    element={
                      <AuthGuard
                        element={<EmployeeManualChangeReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />

                  <Route
                    path="/reporting/report/7"
                    element={
                      <AuthGuard
                        element={<ValidationErrorsSummaryReport />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/reporting/downloadstatus"
                    element={
                      <AuthGuard
                        element={<ReportDownloadStatus />}
                        permission={USER_PERMISSIONS.ViewReports}
                      />
                    }
                  />
                  <Route
                    path="/usermanagement"
                    element={
                      <AuthGuard
                        element={<UserManagement />}
                        permission={USER_PERMISSIONS.ViewUser}
                      />
                    }
                  />
                  <Route
                    path="/usermanagement/new"
                    element={
                      <AuthGuard
                        element={<UserSettings />}
                        permission={USER_PERMISSIONS.ViewUser}
                      />
                    }
                  />
                  <Route
                    path="/usermanagement/:userId"
                    element={
                      <AuthGuard
                        element={<UserSettings />}
                        permission={USER_PERMISSIONS.ViewUser}
                      />
                    }
                  />
                  <Route
                    path="/accountsettings"
                    element={<AuthGuard element={<AccountSettings />} />}
                  />
                </Route>

                {/* Standalone routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          {isDev && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </ConfigProvider>
    </IconContext.Provider>
  );
}

export default App;
